import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Button,
  SxProps,
  Theme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useSearchBox } from "react-instantsearch";
import { usePostHog } from "posthog-js/react";
import { searchBoxStyles } from "../../pages/styles";

/**
 * Props interface for the SearchBox component.
 *
 * @interface SearchBoxProps
 */
export interface SearchBoxProps {
  /** Placeholder text for the search input field */
  placeholder?: string;
  /** Additional styles to apply to the container Box component */
  sx?: SxProps<Theme>;
  /** Whether the input should have a full width */
  fullWidth?: boolean;
  /** Size variant of the TextField component */
  size?: "small" | "medium";
  /** Visual variant of the TextField component */
  variant?: "outlined" | "filled" | "standard";
  /** Whether to show a Search button (default: false, uses instant search) */
  showSearchButton?: boolean;
  /** PostHog event name for tracking searches. If not provided, no tracking occurs. */
  trackingEventName?: string;
}

/**
 * Custom SearchBox component that integrates Material-UI with react-instantsearch.
 *
 * This component provides a search input field with either instant search or
 * search-on-submit behavior, visual feedback for search state, and a clear button.
 *
 * Features:
 * - Instant search (default) or search-on-submit with button
 * - Optional PostHog tracking for search queries (debounced for instant search)
 * - Visual search icon that changes appearance during search
 * - Clear button that appears when there's input
 * - Keyboard support (Enter key handling)
 * - Customizable styling and appearance
 * - Integration with react-instantsearch for search functionality
 *
 * @param props - The component props
 * @param props.placeholder - Placeholder text for the search input (default: "Search...")
 * @param props.sx - Additional styles for the container
 * @param props.fullWidth - Whether the input should be full width (default: true)
 * @param props.size - Size variant of the TextField (default: "small")
 * @param props.variant - Visual variant of the TextField (default: "outlined")
 * @param props.showSearchButton - Whether to show search button (default: false)
 * @param props.trackingEventName - PostHog event name for tracking. No tracking if omitted.
 * @returns {JSX.Element} The search box component
 */
const SearchBox: React.FC<SearchBoxProps> = ({
  placeholder = "Search...",
  sx = {},
  fullWidth = true,
  size = "small",
  variant = "outlined",
  showSearchButton = false,
  trackingEventName,
}) => {
  const { query, refine, clear, isSearchStalled } = useSearchBox();
  const posthog = usePostHog();
  const [inputValue, setInputValue] = useState(query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Tracks a search query in PostHog if tracking is enabled.
   */
  const trackSearch = (searchQuery: string) => {
    if (searchQuery.trim() && trackingEventName) {
      posthog.capture(trackingEventName, {
        query: searchQuery.trim(),
      });
    }
  };

  /**
   * Debounced tracking for instant search mode.
   * Tracks after user stops typing for 1 second.
   */
  useEffect(() => {
    if (!showSearchButton && trackingEventName && inputValue.trim()) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        trackSearch(inputValue);
      }, 1000);
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, showSearchButton, trackingEventName]);

  /**
   * Handles input change events.
   * Updates local state and triggers instant search when button is hidden.
   *
   * @param event - The input change event
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    // Instant search when no button is shown
    if (!showSearchButton) {
      refine(value);
    }
  };

  /**
   * Performs the search and tracks the event in PostHog.
   * Only used when showSearchButton is true.
   */
  const handleSearch = () => {
    refine(inputValue);
    trackSearch(inputValue);
  };

  /**
   * Clears the search input and resets the search results.
   */
  const handleClear = () => {
    setInputValue("");
    clear();
  };

  /**
   * Handles keyboard events for the search input.
   * Triggers search on Enter key press when using search button mode.
   *
   * @param event - The keyboard event
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (showSearchButton) {
        handleSearch();
      }
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, ...sx }}>
      <TextField
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                fontSize="small"
                sx={
                  isSearchStalled
                    ? searchBoxStyles.searchIconStalled
                    : searchBoxStyles.searchIcon
                }
              />
            </InputAdornment>
          ),
          endAdornment: inputValue && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={searchBoxStyles.clearButton}
                aria-label="Clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ ...searchBoxStyles.textField, flex: 1 }}
      />
      {showSearchButton && (
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!inputValue.trim()}
          disableElevation
          disableRipple
          sx={{
            minWidth: "auto",
            px: 3,
            borderRadius: "10px",
            fontWeight: "bold",
            bgcolor: "primary.main",
            opacity: 0.9,
            "&:hover": {
              bgcolor: "primary.main",
              opacity: 1,
            },
          }}
        >
          Search
        </Button>
      )}
    </Box>
  );
};

export default SearchBox;
